import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export const SUB_STATUS = {
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  ERROR: 'ERROR',
  RECONNECTING: 'RECONNECTING',
  FAILED: 'FAILED',
};

const MAX_RETRIES = 5;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;

/**
 * useSubscriptionStatus
 *
 * @param {object} options
 * @param {string}   options.channelName   - Unique Supabase channel name
 * @param {object}   options.filter        - { event, schema, table, filter }
 * @param {function} options.onData        - Callback(payload) for data events
 * @param {function} options.onReconnect   - Called after successful reconnect (re-fetch trigger)
 * @param {boolean}  options.enabled       - Whether to subscribe (e.g. user logged in)
 * @returns {{ status, subWarningBanner, retryNow }}
 */
export function useSubscriptionStatus({ channelName, filter, onData, onReconnect, enabled = true }) {
  const [status, setStatus] = useState(SUB_STATUS?.CONNECTING);
  const channelRef = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const onDataRef = useRef(onData);
  const onReconnectRef = useRef(onReconnect);

  // Keep refs up-to-date on every render without triggering re-subscriptions
  useEffect(() => {
    onDataRef.current = onData;
  });
  useEffect(() => {
    onReconnectRef.current = onReconnect;
  });

  const clearRetryTimer = () => {
    if (retryTimerRef?.current) {
      clearTimeout(retryTimerRef?.current);
      retryTimerRef.current = null;
    }
  };

  const removeChannel = useCallback(() => {
    if (channelRef?.current) {
      supabase?.removeChannel(channelRef?.current);
      channelRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!mountedRef?.current || !enabled) return;

    removeChannel();

    // Strip undefined filter property — Supabase realtime crashes if filter is undefined
    const safeFilter = { ...filter };
    if (safeFilter?.filter === undefined) {
      delete safeFilter?.filter;
    }

    const ch = supabase
      ?.channel(channelName)
      ?.on('postgres_changes', safeFilter, (payload) => {
        onDataRef?.current?.(payload);
      });

    // Subscribe and handle status
    ch?.subscribe((subStatus, err) => {
      if (!mountedRef?.current) return;

      if (subStatus === 'SUBSCRIBED') {
        const wasReconnecting = retryCountRef?.current > 0;
        retryCountRef.current = 0;
        clearRetryTimer();
        setStatus(SUB_STATUS?.CONNECTED);
        if (wasReconnecting) {
          onReconnectRef?.current?.();
        }
      } else if (subStatus === 'CHANNEL_ERROR' || subStatus === 'TIMED_OUT' || err) {
        console.error(`[Subscription] Error on channel "${channelName}":`, subStatus, err);
        handleError();
      } else if (subStatus === 'CLOSED') {
        if (mountedRef?.current && retryCountRef?.current < MAX_RETRIES) {
          handleError();
        }
      }
    });

    channelRef.current = ch;
  }, [channelName, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleError = useCallback(() => {
    if (!mountedRef?.current) return;

    if (retryCountRef?.current >= MAX_RETRIES) {
      setStatus(SUB_STATUS?.FAILED);
      console.error(`[Subscription] Permanent failure on channel "${channelName}" after ${MAX_RETRIES} retries.`);
      return;
    }

    setStatus(retryCountRef?.current === 0 ? SUB_STATUS?.ERROR : SUB_STATUS?.RECONNECTING);
    retryCountRef.current += 1;

    const delay = Math.min(BASE_DELAY_MS * Math.pow(2, retryCountRef?.current - 1), MAX_DELAY_MS);
    console.warn(`[Subscription] Retrying "${channelName}" in ${delay}ms (attempt ${retryCountRef?.current}/${MAX_RETRIES})`);

    clearRetryTimer();
    retryTimerRef.current = setTimeout(() => {
      if (mountedRef?.current) {
        setStatus(SUB_STATUS?.RECONNECTING);
        connect();
      }
    }, delay);
  }, [channelName, connect]);

  const retryNow = useCallback(() => {
    retryCountRef.current = 0;
    clearRetryTimer();
    setStatus(SUB_STATUS?.CONNECTING);
    connect();
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) {
      connect();
    }
    return () => {
      mountedRef.current = false;
      clearRetryTimer();
      removeChannel();
    };
  }, [enabled, connect, removeChannel]);

  // Derive banner state from status
  const subWarningBanner = (() => {
    if (status === SUB_STATUS?.ERROR || status === SUB_STATUS?.RECONNECTING) {
      return { visible: true, type: 'warning', message: 'Conexión en tiempo real interrumpida. Reintentando...' };
    }
    if (status === SUB_STATUS?.FAILED) {
      return { visible: true, type: 'error', message: 'No se pudo reconectar. Los datos pueden no estar actualizados.' };
    }
    return { visible: false, type: null, message: null };
  })();

  return { status, subWarningBanner, retryNow };
}
